// src/hooks/useRateLimit.js

import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for rate limiting actions
 * @param {string} key - Unique identifier for the rate limit
 * @param {number} maxAttempts - Maximum number of attempts allowed
 * @param {number} windowMs - Time window in milliseconds
 * @returns {object} - { canAttempt, attemptAction, remainingAttempts, resetTime }
 */
export default function useRateLimit(key, maxAttempts = 5, windowMs = 15 * 60 * 1000) {
  const [attempts, setAttempts] = useState(0);
  const [resetTime, setResetTime] = useState(null);

  // Load existing attempts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`rateLimit_${key}`);
    if (stored) {
      const data = JSON.parse(stored);
      const now = Date.now();
      
      // Check if window has expired
      if (now - data.firstAttempt > windowMs) {
        localStorage.removeItem(`rateLimit_${key}`);
        setAttempts(0);
        setResetTime(null);
      } else {
        setAttempts(data.count);
        setResetTime(data.firstAttempt + windowMs);
      }
    }
  }, [key, windowMs]);

  const canAttempt = attempts < maxAttempts;
  const remainingAttempts = Math.max(0, maxAttempts - attempts);

  const attemptAction = useCallback(() => {
    if (!canAttempt) return false;

    const now = Date.now();
    const stored = localStorage.getItem(`rateLimit_${key}`);
    let data;

    if (stored) {
      data = JSON.parse(stored);
      // Reset if window expired
      if (now - data.firstAttempt > windowMs) {
        data = { firstAttempt: now, count: 1 };
      } else {
        data.count += 1;
      }
    } else {
      data = { firstAttempt: now, count: 1 };
    }

    localStorage.setItem(`rateLimit_${key}`, JSON.stringify(data));
    setAttempts(data.count);
    setResetTime(data.firstAttempt + windowMs);

    return data.count <= maxAttempts;
  }, [canAttempt, key, maxAttempts, windowMs]);

  // Reset attempts when window expires
  useEffect(() => {
    if (!resetTime) return;

    const timeout = setTimeout(() => {
      localStorage.removeItem(`rateLimit_${key}`);
      setAttempts(0);
      setResetTime(null);
    }, resetTime - Date.now());

    return () => clearTimeout(timeout);
  }, [resetTime, key]);

  return {
    canAttempt,
    attemptAction,
    remainingAttempts,
    resetTime
  };
}
